<script setup>
import { useFormatters } from '../composables/useFormatters'

const { fmtKzt } = useFormatters()

const props = defineProps({
  row:            { type: Object, required: true },
  date:           { type: Object, required: true },
  todayIso:       { type: String, required: true },
  isFutureDate:   { type: Boolean, required: true },
  cellVal:        { type: [String, Number], default: null },
  cellMeta:       { type: Object, default: () => ({}) },
  parentCellData: { type: Object, default: null },
  groupCompletion:{ type: String, default: 'none' },
  isWorkComplete: { type: Boolean, default: false },
  isSmetaBlocked: { type: Boolean, default: false },
  stornoMode:     { type: Boolean, default: false },
})

const emit = defineEmits(['open-fact-dialog', 'open-group-fact-dialog', 'delete-fact'])

function hasVal() {
  return props.cellVal !== '' && props.cellVal !== null && props.cellVal !== undefined
}

function cellClass() {
  const hv = hasVal()
  if (props.isSmetaBlocked && !hv) return { 'cell-click--smeta-blocked': true }
  if (props.isSmetaBlocked && hv) return { 'cell-click--filled': true, 'cell-click--smeta-blocked': true }
  if (props.isWorkComplete && !hv) return { 'cell-click--done': true }
  if (!hv) return {}
  const meta = props.cellMeta || {}
  const factNum = parseFloat(props.cellVal) || 0
  const planNum = parseFloat(props.row.work_set_amount) || 0
  const isOver = planNum > 0 && factNum > planNum
  const classes = {
    'cell-click--filled': true,
    'cell-click--sent':  !!meta.isSent && !isOver,
    'cell-click--saved': !meta.isSent  && !isOver,
    'cell-click--over':  isOver,
    'cell-click--pending-correction': !!meta.hasPendingCorrection,
  }
  if (props.stornoMode && hv) {
    classes['cell-click--storno-target'] = true
  }
  return classes
}

function isDateClickable() {
  return props.isFutureDate || props.date.iso <= props.todayIso
}

function onWorkClick() {
  if (props.isSmetaBlocked) {
    emit('open-fact-dialog', props.row, props.date)
    return
  }
  if (!isDateClickable()) return
  if (props.isWorkComplete && !hasVal()) return

  if (props.stornoMode && hasVal()) {
    const meta = props.cellMeta || {}
    if (!meta.isSent) {
      emit('delete-fact', props.row, props.date)
      return
    }
  }

  emit('open-fact-dialog', props.row, props.date)
}

function onGroupClick() {
  if (props.isSmetaBlocked) {
    emit('open-group-fact-dialog', props.row, props.date)
    return
  }
  if (!isDateClickable()) return
  emit('open-group-fact-dialog', props.row, props.date)
}

function onGroupDoneClick() {
  emit('open-group-fact-dialog', props.row, props.date)
}
</script>

<template>
  <td
    v-memo="[cellVal, cellMeta?.isSent, cellMeta?.hasPendingCorrection, cellMeta?.factPrice, cellMeta?.materialsPriceSmr, cellMeta?.materialsPriceTmc, parentCellData?.hasData, parentCellData?.smr, parentCellData?.tmc, groupCompletion, isWorkComplete, isSmetaBlocked, stornoMode]"
    class="td-day"
    style="width: 80px; min-width: 72px"
    :class="{
      'td-day--weekend': date.isWeekend,
      'td-day--today':   date.iso === todayIso,
      'td-ro':           row.node_type !== 'work' && row.node_type !== 'group',
    }"
  >
    <!-- Работа: обычный ввод факта -->
    <div
      v-if="row.node_type === 'work'"
      class="cell-click"
      :class="[cellClass(), { 'cell-click--future': !isFutureDate && date.iso > todayIso }]"
      @click="onWorkClick"
    >
      <template v-if="hasVal()">
        <span class="cell-click__val">{{ cellVal }}</span>
        <span class="cell-work__smr">
          <span class="cell-parent__label">СМР</span> {{ fmtKzt((cellMeta?.factPrice ?? 0) + (cellMeta?.materialsPriceSmr ?? 0)) }} ₸
        </span>
        <span class="cell-work__tmc">
          <span class="cell-parent__label">ТМЦ</span> {{ fmtKzt(cellMeta?.materialsPriceTmc ?? 0) }} ₸
        </span>
        <span v-if="cellMeta?.hasPendingCorrection" class="cell-click__badge cell-click__badge--correction" title="Есть ожидающая корректировка">кор</span>
        <span v-else-if="cellMeta?.isSent" class="cell-click__badge">усп</span>
        <span
          v-else-if="!row.usp_work_guid"
          class="cell-click__badge cell-click__badge--no-usp"
          title="Работа УСП не найдена, отправка по интеграции невозможна"
        >!усп</span>
      </template>
      <template v-else-if="isSmetaBlocked">
        <q-icon name="lock" size="12px" color="red-4" />
      </template>
      <span v-else class="cell-click__empty">+</span>
    </div>

    <!-- Конструктив (группа): все работы завершены -->
    <div
      v-else-if="row.node_type === 'group' && groupCompletion === 'all'"
      class="cell-click cell-click--done cell-click--done-group"
      @click="onGroupDoneClick"
    >
      <template v-if="parentCellData?.hasData">
        <span
          class="cell-parent__smr"
          :title="'СМР: ' + fmtKzt(parentCellData.smr) + ' ₸'"
        >
          <span class="cell-parent__label">СМР</span> {{ fmtKzt(parentCellData.smr) }} ₸
        </span>
        <span
          class="cell-parent__tmc"
          :title="'ТМЦ: ' + fmtKzt(parentCellData.tmc) + ' ₸'"
        >
          <span class="cell-parent__label">ТМЦ</span> {{ fmtKzt(parentCellData.tmc) }} ₸
        </span>
      </template>
    </div>

    <!-- Конструктив (группа): кликабельно -->
    <div
      v-else-if="row.node_type === 'group'"
      class="cell-click cell-click--group"
      :class="{
        'cell-click--future': !isFutureDate && date.iso > todayIso,
        'cell-click--smeta-blocked': isSmetaBlocked,
      }"
      @click="onGroupClick"
    >
      <template v-if="parentCellData?.hasData">
        <span
          class="cell-parent__smr"
          :title="'СМР: ' + fmtKzt(parentCellData.smr) + ' ₸'"
        >
          <span class="cell-parent__label">СМР</span> {{ fmtKzt(parentCellData.smr) }} ₸
        </span>
        <span
          class="cell-parent__tmc"
          :title="'ТМЦ: ' + fmtKzt(parentCellData.tmc) + ' ₸'"
        >
          <span class="cell-parent__label">ТМЦ</span> {{ fmtKzt(parentCellData.tmc) }} ₸
        </span>
      </template>
      <template v-else-if="isSmetaBlocked">
        <q-icon name="lock" size="12px" color="red-4" />
      </template>
      <span v-else class="cell-click__empty cell-click__empty--group">+</span>
    </div>

    <!-- Родительский узел: только чтение -->
    <div v-else class="cell-ro cell-ro--parent">
      <template v-if="parentCellData?.hasData">
        <span
          class="cell-parent__smr"
          :title="'СМР: ' + fmtKzt(parentCellData.smr) + ' ₸'"
        >
          <span class="cell-parent__label">СМР</span> {{ fmtKzt(parentCellData.smr) }} ₸
        </span>
        <span
          class="cell-parent__tmc"
          :title="'ТМЦ: ' + fmtKzt(parentCellData.tmc) + ' ₸'"
        >
          <span class="cell-parent__label">ТМЦ</span> {{ fmtKzt(parentCellData.tmc) }} ₸
        </span>
      </template>
    </div>
  </td>
</template>
